import { useState } from "react";
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { PLAIN_BUTTONS } from "./buttons";
import type { Row } from "./checklistRows";

interface Props {
  companyName: string;
  rows: Row[];
  saving: boolean;
  onClose: () => void;
  onAsk: (rows: Row[]) => Promise<string | null>;
}

export default function AskClientDialog({ companyName, rows, saving, onClose, onAsk }: Props) {
  const phone = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const ticked = rows.filter((r) => !unticked.has(r.key));

  const toggle = (key: string) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Dialog open fullWidth maxWidth="sm" fullScreen={phone} sx={PLAIN_BUTTONS} onClose={(_, reason) => { if (reason !== "backdropClick") onClose(); }}>
      <DialogTitle sx={{ pb: 0.5 }}>Ask the client</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <span className="text-sm text-gray-500">Everything still to do from {companyName}. Untick what you're not asking for yet.</span>
          {error && <Alert severity="error">{error}</Alert>}
          <ul aria-label="Client steps" className="rounded-lg border border-gray-100 dark:border-gray-800">
            {rows.map((r) => {
              const brands = r.items.filter((i) => i.state === "todo").flatMap((i) => i.brandName ?? []);
              return (
                <li key={r.key} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                  <label className="flex cursor-pointer items-start gap-2 px-2 py-1.5 text-sm text-gray-800 dark:text-white/90">
                    <Checkbox size="small" checked={!unticked.has(r.key)} onChange={() => toggle(r.key)} sx={{ p: 0.5 }} />
                    <span className="pt-1">
                      {r.title}
                      {brands.length > 0 && <span className="text-gray-500"> · {brands.join(", ")}</span>}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !ticked.length} onClick={async () => setError(await onAsk(ticked))}>Mark {ticked.length} asked</Button>
      </DialogActions>
    </Dialog>
  );
}
